<template>
    <div class="container pb-4">
        <div class="d-flex justify-content-center spinner-border primary-icon spinner-position" v-if="page.isLoading"></div>
        <div class="spinner" v-if="page.isLoading"></div>
        <o-bread-crumb>
            <template v-slot:pageName>
                Corporate
            </template>
            <template v-slot:pageDetail>
                Transaction Report
            </template>
        </o-bread-crumb>

        <FeatureGate featureKey="transaction_report_module">
            <div v-if="error">
                <div class="alert alert-danger mt-1" role="alert">
                    {{ error }}
                </div>
            </div>
            <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item">
                        <div class="d-flex d-inline-block">
                            <h4 class="header-title">
                                <i class="fas fa-chart-bar me-2 primary-icon"></i>Transaction Report
                            </h4>
                        </div>
                        <p class="header-subtitle me-4 mt-1">Display transaction report from each merchant/outlet per day in certain period</p>
                    </li>
                </ul>
            </div>
            <div class="card-body">
                <!-- Filter & Action Bar -->
                <div class="filter-action-wrapper mb-4 mt-3">
                    <div class="filter-controls">
                        <!-- Branch Filter -->
                        <div class="filter-item">
                            <div class="section-label-tabs">
                                <span class="label-tab">Branch</span>
                            </div>
                            <div class="date-range-box">
                                <select class="form-select form-control-filter" v-model="selectedBranch" @change="handleBranchChange">
                                    <option v-if="showBranchPlaceholder" value="" disabled selected>Select Branch</option>
                                    <option v-else-if="!showBranchPlaceholder && !branchDashboard" value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Merchant Filter -->
                        <div class="filter-item">
                            <div class="section-label-tabs">
                                <span class="label-tab">Merchant</span>
                            </div>
                            <div class="date-range-box">
                                <select class="form-select form-control-filter" v-model="selectedMerchant" @change="handleMerchantChange">
                                    <option v-if="showMerchantPlaceholder" value="" disabled selected>Select Merchant</option>
                                    <option v-else value="">All Merchant</option>
                                    <option v-for="merchant in merchants" :key="merchant.id" :value="merchant.id">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Date Range Filter -->
                        <div class="filter-item">
                            <div class="section-label-tabs">
                                <span class="label-tab">Date Range</span>
                            </div>
                            <div class="date-range-box">
                                <div class="date-input-wrapper">
                                    <input
                                        placeholder="Select Date Range"
                                        ref="datePicker"
                                        class="form-control-filter"
                                    />
                                    <i class="fas fa-calendar-alt calendar-icon-filter"></i>
                                </div>
                            </div>
                        </div>

                        <!-- Sort Toggle -->
                        <div class="filter-item">
                            <div class="section-label-tabs">
                                <span class="label-tab">Sort</span>
                            </div>
                            <div class="date-range-box">
                                <div class="sort-toggle-box">
                                    <span class="sort-label" :class="{ active: !isDesc }">ASC</span>
                                    <label class="switch-sm">
                                        <input type="checkbox" v-model="isDesc">
                                        <span class="slider-sm round"></span>
                                    </label>
                                    <span class="sort-label" :class="{ active: isDesc }">DESC</span>
                                </div>
                            </div>
                        </div>

                        <!-- Point Manipulation Toggle -->
                        <div class="filter-item" v-if="isPointSystemActive">
                            <div class="section-label-tabs">
                                <span class="label-tab">Point Manipulation</span>
                            </div>
                            <div class="date-range-box">
                                <div class="sort-toggle-box">
                                    <span class="sort-label" :class="{ active: !showPointManipulation }">Hide</span>
                                    <label class="switch-sm">
                                        <input type="checkbox" v-model="showPointManipulation">
                                        <span class="slider-sm round"></span>
                                    </label>
                                    <span class="sort-label" :class="{ active: showPointManipulation }">Show</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="action-section">
                        <div class="action-buttons-group">
                            <button @click="applyFilter" class="btn btn-apply-filter">
                                <i class="fas fa-filter me-1"></i> Apply
                            </button>
                            <button v-if="isFilterApplied" @click="clearFilter" class="btn btn-clear-filter" title="Clear Filter">
                                <i class="fas fa-times me-1"></i> Clear
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Export Bar -->
                <div class="export-action-wrapper mb-4">
                    <div class="export-format-group">
                        <span class="export-label">Export as:</span>
                        <div class="format-buttons">
                            <button
                                class="format-btn"
                                :class="{ active: selectedExportFormat === 'excel' }"
                                @click="selectedExportFormat = 'excel'"
                            >Excel</button>
                            <button
                                class="format-btn"
                                :class="{ active: selectedExportFormat === 'pdf' }"
                                @click="selectedExportFormat = 'pdf'"
                            >PDF</button>
                            <button
                                class="format-btn"
                                :class="{ active: selectedExportFormat === 'word' }"
                                @click="selectedExportFormat = 'word'"
                            >Word</button>
                        </div>
                    </div>
                    <button class="btn btn-export" @click.prevent="download_state" :disabled="isExporting">
                        <i v-if="!isExporting" class="fas fa-download me-1"></i>
                        {{ isExporting ? 'Exporting...' : 'Export' }}
                    </button>
                </div>

                <!-- Data Table -->
                <o-table style="text-align:left;" class="table-hover report-list-table">
                    <thead>
                        <tr>
                            <td class="fw-bolder text-muted" scope="col">No</td>
                            <td class="fw-bolder text-muted" scope="col">Date</td>
                            <td class="fw-bolder text-muted" scope="col">Branch</td>
                            <td class="fw-bolder text-muted" scope="col">Merchant</td>
                            <td class="fw-bolder text-muted text-end" scope="col">Sum Transaction</td>
                            <td class="fw-bolder text-muted text-end" scope="col" v-if="isPointSystemActive">Point In</td>
                            <td class="fw-bolder text-muted text-end" scope="col" v-if="isPointSystemActive">Point Out</td>
                            <td class="fw-bolder text-muted text-center" scope="col">Action</td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(card, index) in visibleCards" :key="index" class="report-row-clickable" @click="navigateToDetail(card)">
                            <td><span class="date-text">{{ (meta.page - 1) * meta.limit + index + 1 }}</span></td>
                            <td>
                                <span class="date-text">{{ card.timestamp }}</span>
                            </td>
                            <td>
                                <span class="branch-text">{{ card.branchName }}</span>
                                <p v-if="card.branchName === card.outletName" class="extra-note mb-0">
                                    Point Manipulation Corporate
                                </p>
                            </td>
                            <td>
                                <span class="merchant-text">{{ card.outletName }}</span>
                                <p v-if="card.corporateName === '00000000-0000-0000-0000-000000000000' && card.branchName !== card.outletName" class="extra-note mb-0">
                                    Point Manipulation Merchant
                                </p>
                            </td>
                            <td class="text-end">
                                <span class="amount-positive">{{ card.totalTransaction != 0 ? `+ ${card.totalTransaction}` : card.totalTransaction }}</span>
                            </td>
                            <td class="text-end" v-if="isPointSystemActive">
                                <span class="point-in">{{ card.pointIn != 0 ? `+ ${card.pointIn}` : card.pointIn }}</span>
                            </td>
                            <td class="text-end" v-if="isPointSystemActive">
                                <span class="point-out">{{ card.pointOut != 0 ? `- ${card.pointOut}` : card.pointOut }}</span>
                            </td>
                            <td class="text-center">
                                <button class="btn btn-action btn-detail" @click.stop="navigateToDetail(card)">
                                    <i class="fas fa-eye me-1"></i> Details
                                </button>
                            </td>
                        </tr>
                        <tr v-if="visibleCards.length === 0">
                            <td :colspan="isPointSystemActive ? 8 : 6" class="text-center">No transaction data found</td>
                        </tr>
                    </tbody>
                </o-table>

                <!-- Summary Footer -->
                <div class="total-summary mt-4 mb-3">
                    <div class="row g-3">
                        <div :class="isPointSystemActive ? 'col-md-3' : 'col-md-12'">
                            <div :class="isPointSystemActive ? 'summary-card' : 'summary-card summary-card-inline'">
                                <div class="summary-content">
                                    <div class="summary-icon icon-amount">
                                        <i class="fas fa-coins"></i>
                                    </div>
                                    <div class="summary-details">
                                        <p class="summary-label-text">Total Amount</p>
                                        <h3 v-if="isPointSystemActive" class="summary-value-text amount-positive">
                                            <span v-if="totalSumTransaction != 0">+ </span>{{ totalSumTransaction }}
                                        </h3>
                                    </div>
                                    <div v-if="!isPointSystemActive" class="summary-inline-value">
                                        <h3 class="summary-value-text amount-positive">
                                            <span v-if="totalSumTransaction != 0">+ </span>{{ totalSumTransaction }}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="isPointSystemActive">
                            <div class="summary-card">
                                <div class="summary-content">
                                    <div class="summary-icon icon-point-in">
                                        <i class="fas fa-arrow-down"></i>
                                    </div>
                                    <div class="summary-details">
                                        <p class="summary-label-text">Point Incoming</p>
                                        <h3 class="summary-value-text point-in">
                                            {{ totalPointIn != 0 ? `+ ${totalPointIn}` : totalPointIn }}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="isPointSystemActive">
                            <div class="summary-card">
                                <div class="summary-content">
                                    <div class="summary-icon icon-point-out">
                                        <i class="fas fa-arrow-up"></i>
                                    </div>
                                    <div class="summary-details">
                                        <p class="summary-label-text">Point Deducted</p>
                                        <h3 class="summary-value-text point-out">
                                            {{ totalPointOut != 0 ? `- ${totalPointOut}` : totalPointOut }}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="isPointSystemActive">
                            <div class="summary-card">
                                <div class="summary-content">
                                    <div class="summary-icon icon-balance">
                                        <i class="fas fa-balance-scale"></i>
                                    </div>
                                    <div class="summary-details">
                                        <p class="summary-label-text">Point Balance</p>
                                        <h3 class="summary-value-text" :class="balance > 0 ? 'point-in' : balance < 0 ? 'point-out' : ''">
                                            {{ balanceDisplay }}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="container mt-5">
                    <o-pagination :page="meta.page" :total="meta.total" @callback="navPage"/>
                </div>
            </div>
        </div>
        </FeatureGate>
    </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, reactive, watch, computed } from 'vue';
import { OBreadCrumb, OPagination, OTable } from '@/components/OmnihControl';
import FeatureGate from '@/components/upsell/FeatureGate.vue';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { usePointSystem } from '@/composables/pointsystem';
import { useToast } from '@/composables/useToast';

export default {
    components: {
        OBreadCrumb,
        OPagination,
        OTable,
        FeatureGate,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const { isPointSystemActive } = usePointSystem();
        const { showToast } = useToast();
        let meta = computed(() => store.state.TransactionReport.meta)

        const page = reactive({
            isLoading: false,
        });

        const bottomContainer = ref(null);
        const customScrollbar = ref(null);
        const scrollPercentage = ref(100);
        
        const corporateId = ref('');
        const cards = ref([]);
        const visibleCards = ref([]);
        const branches = ref([]);
        const merchants = ref([]);
        const dateRange = ref([null, null]);
        const datePicker = ref(null);
        const isDesc = ref(true);
        const isExporting = ref(false);
        const showPointManipulation = ref(true);
        const selectedExportFormat = ref('excel');

        const totalSumTransaction = ref(0);
        const totalPointIn = ref('0');
        const totalPointOut = ref('0');

        const balance = computed(() => {
            const cleanTotalPointIn = parseInt(totalPointIn.value.replace(/\D/g, ''), 10);
            const cleanTotalPointOut = parseInt(totalPointOut.value.replace(/\D/g, ''), 10);
            return cleanTotalPointIn - cleanTotalPointOut;
        });

        const error = ref('');
        const thisComponentShow = ref(true);

        // Check if transaction report module is enabled via feature flag from database
        const isTransactionReportEnabled = computed(() => {
            return (store.state.FeatureFlag?.transactionReportModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const columns = computed(() => [
            'Date',
            'Branch Merchant',
            'Sum Transaction',
            ...(isPointSystemActive.value ? ['Point In', 'Point Out'] : []),
        ]);

        const isFilterApplied = ref(false); 

        let flatpickrInstance = null;

        onMounted(() => {
            if (datePicker.value) {
                flatpickrInstance = flatpickr(datePicker.value, {
                    mode: 'range',
                    maxRange: 31,
                    onChange: handleDateRangeChange,
                });
            }

            load_state();
            syncScroll();
            window.addEventListener('resize', checkMobileView);
        });

        onBeforeUnmount(() => {
            store.commit('TransactionReport/reset_meta');
            window.removeEventListener('resize', checkMobileView);
        });

        const syncScroll = () => {
            if (!bottomContainer.value) return;
            const maxScrollTop = bottomContainer.value.scrollHeight - bottomContainer.value.clientHeight;
            if (maxScrollTop > 0) {
                const invertedScrollTop = maxScrollTop - bottomContainer.value.scrollTop;
                scrollPercentage.value = Math.round((invertedScrollTop / maxScrollTop) * 100);
            } else {
                scrollPercentage.value = 100;
            }
        };

        const syncCustomScrollbar = () => {
            if (!bottomContainer.value) return;
            const maxScrollTop = bottomContainer.value.scrollHeight - bottomContainer.value.clientHeight;
            if (maxScrollTop > 0) {
                const invertedScrollTop = maxScrollTop - Math.round((scrollPercentage.value / 100) * maxScrollTop);
                bottomContainer.value.scrollTop = invertedScrollTop;
            }
        };

        watch(
            () => visibleCards,
            () => {
                syncScroll();
            },
            { immediate: true }
        );

        function load_state() {
            const flag = store.getters['FeatureFlag/getTransactionReportModuleFlag'] || 'ACTIVE';
            if (flag === 'NONACTIVE') {
                const content = store.state['feature-upsell']?.contents?.['transaction_report_module'];
                if (!!content && content.is_active !== false) {
                    page.isLoading = false;
                    return;
                }
            }

            page.isLoading = true;

            store.dispatch('Auth/getBusinessProfile')
            .then(businessProfile => {
                
                const filter = {
                    corporate_id: businessProfile.corporate_id,
                }

                if (businessProfile.branch_id) {
                    filter.branch_id = businessProfile.branch_id;
                }

                if (selectedBranch.value) {
                    filter.branch_id = selectedBranch.value;
                }

                if (selectedMerchant.value) {
                    filter.merchant_id = selectedMerchant.value;
                }

                if (dateRange.value[0] && dateRange.value[1]) {
                    const formattedDateRange = formatDateRange(dateRange.value);
                    filter.dateRange = formattedDateRange;
                } else {
                    const initDateRange = initializeDateRange();
                    filter.dateRange = initDateRange;
                }

                filter.sortOrder = isDesc.value ? 'desc' : 'asc';
                filter.showPointManipulation = showPointManipulation.value ? 'true' : 'false';

                filter.isFilterApplied = isFilterApplied.value;

                store.commit('TransactionReport/add_filter', {filter});

                store.dispatch('TransactionReport/read')
                .then((resp) => {
                    cards.value = resp.data.map(card => ({
                        ...card,
                        totalTransaction: formatNumber(card.totalTransaction),
                        pointIn: formatNumber(card.pointIn),
                        pointOut: formatNumber(card.pointOut),
                    }));

                    if (businessProfile.branch_id) {
                        branches.value = resp.branches.filter(branch => branch.id === businessProfile.branch_id);
                        selectedBranch.value = businessProfile.branch_id;
                        showBranchPlaceholder.value = false;
                        branchDashboard.value = true;
                        const selected = branches.value.find(b => b.id === selectedBranch.value);
                        if (selected?.merchant) {
                            updateMerchants(selected.merchant);
                        }
                    } else {
                        branches.value = resp.branches;
                        branchDashboard.value = false;
                    }
                    
                    totalSumTransaction.value = formatNumber(resp.totalSumTransaction);
                    totalPointIn.value = formatNumber(resp.totalPointIn);
                    totalPointOut.value = formatNumber(resp.totalPointOut);
                    error.value = ''; 

                    if (resp.dateRange) {
                        const { dateFrom, dateTo } = resp.dateRange;
                        const formattedDateFrom = formatDate(new Date(dateFrom));
                        const formattedDateTo = formatDate(new Date(dateTo));

                        dateRange.value = [new Date(dateFrom), new Date(dateTo)];

                        if (flatpickrInstance) {
                            flatpickrInstance.setDate([new Date(dateFrom), new Date(dateTo)]);

                            if (flatpickrInstance.input) {
                                flatpickrInstance.input.value = `${formattedDateFrom} - ${formattedDateTo}`;
                            }
                        }
                    }
                    page.isLoading = false;
                })
                .catch((err) => {
                    error.value = (err?.response?.data?.error === "" || err?.response?.data?.error === "[]") 
                                    ? err?.response?.data?.message 
                                    : err?.response?.data?.error;
                    if (error.value == 'This account does not have the required permission') {
                        thisComponentShow.value = false;
                    }
                    page.isLoading = false;
                    console.error(err)
                });
            })
            .catch((err) => {
                page.isLoading = false;
                console.error(err);
            });
        }

        function navPage(page){
            const _meta = {... meta.value, page}
            scrollPercentage.value = 100;
            if (bottomContainer.value) {
                bottomContainer.value.scrollTop = 0;
            }
            syncScroll();

            console.log({page});
            console.log({_meta});
            store.commit('TransactionReport/switch_page',{meta:_meta})
            load_state()
        }

        const formatNumber = (value) => {
            const intValue = parseInt(value, 10) || 0;
            return intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        };

        watch(cards, (newCards) => {            
            visibleCards.value = newCards.slice(0, 10);
            page.isLoading = false;
        });

        const formatPrice = (value) => {
            return value.toLocaleString('id-ID');
        };

        const branchDashboard = ref(false);

        const selectedBranch = ref('');
        const selectedMerchant = ref('');
        
        const showBranchPlaceholder = ref(true);
        const showMerchantPlaceholder = ref(true);

        const handleBranchChange = () => {
            if (selectedBranch.value === '') {
                showBranchPlaceholder.value = true;
                merchants.value = [];
                selectedMerchant.value = '';
                showMerchantPlaceholder.value = true;
            } else {
                showBranchPlaceholder.value = false;
                const selected = branches.value.find(b => b.id === selectedBranch.value);
                selectedMerchant.value = '';
                showMerchantPlaceholder.value = true;
                
                if (selected?.merchant) {
                    updateMerchants(selected.merchant);
                }
            }
        };

        const handleMerchantChange = () => {
            if (selectedMerchant.value === '') {
                showMerchantPlaceholder.value = true;
            } else {
                showMerchantPlaceholder.value = false;
            }
        };

        const handleDateRangeChange = (selectedDates, dateStr, instance) => {
            dateRange.value = [null, null];
            if (selectedDates.length === 2) {
                const formattedStartDate = formatDate(selectedDates[0]);
                const formattedEndDate = formatDate(selectedDates[1]);

                const startDate = new Date(formattedStartDate);
                const endDate = new Date(formattedEndDate);

                const diffTime = Math.abs(endDate - startDate);
                let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                if (diffDays > 31) {
                    const newEndDate = new Date(startDate);
                    newEndDate.setDate(startDate.getDate() + 30);

                    dateRange.value = [selectedDates[0], newEndDate];

                    instance.setDate([selectedDates[0], newEndDate]);

                    const formattedNewEndDate = formatDate(newEndDate);
                    instance.input.value = `${formattedStartDate} - ${formattedNewEndDate}`;

                    showToast({
                        type: 'info',
                        title: 'Date Range Adjusted',
                        message: 'Maximum date range is 31 days. The end date has been automatically adjusted.',
                    });
                } else{
                    dateRange.value = selectedDates;
                    instance.input.value = `${formattedStartDate} - ${formattedEndDate}`;
                }
            }
        };

        const formatDate = (date) => {
            const day = date.getDate();
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        };

        const toggleBranchPlaceholder = () => {
            if (showBranchPlaceholder.value) {
                showBranchPlaceholder.value = false;
            }
        };

        const toggleMerchantPlaceholder = () => {
            if (showMerchantPlaceholder.value) {
                showMerchantPlaceholder.value = false;
            }
        };

        const updateMerchants = (allMerchant) => {
            merchants.value = allMerchant;            
        };

        const applyFilter = () => {
            page.isLoading = true;
            isFilterApplied.value = true;
            scrollPercentage.value = 100;
            if (bottomContainer.value) {
                bottomContainer.value.scrollTop = 0;
            }
            syncScroll();
            const _meta = { ...meta.value, page: 1 };
            store.commit('TransactionReport/switch_page',{meta:_meta})
            load_state();            
        };

        const formatDateRange = (date) => {
            const parsedDate = date.map(dateObj => {
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }).join(', ');

            return parsedDate;
        }

        const clearFilter = () => {
            scrollPercentage.value = 100;
            isFilterApplied.value = false;
            if (bottomContainer.value) {
                bottomContainer.value.scrollTop = 0;
            }
            syncScroll();
            selectedBranch.value = '';
            selectedMerchant.value = '';
            merchants.value = [];
            dateRange.value = [null, null];
            showBranchPlaceholder.value = true;
            showMerchantPlaceholder.value = true;
            isDesc.value = true;
            showPointManipulation.value = true;
            const _meta = { ...meta.value, page: 1 };
            store.commit('TransactionReport/switch_page',{meta:_meta})
            clearDateRange();
            error.value = '';
            load_state();
        };

        const clearDateRange = () => {
            if (flatpickrInstance) {
                flatpickrInstance.clear();

                if (flatpickrInstance.input) {
                    flatpickrInstance.input.value = ''; // Clears the input field
                }
            }

            dateRange.value = [null, null];
        };

        const isMobile = ref(window.innerWidth <= 768);

        const checkMobileView = () => {
            isMobile.value = window.innerWidth <= 768;
        };

        const balanceClass = computed(() => {
            if (balance.value < 0) {
                return 'negative-not-amount';
            } else if (balance.value > 0) {
                return 'positive-not-amount';
            } else {
                return 'zero-out-not-amount';
            }
        });

        const balanceDisplay = computed(() => {
            const formattedBalance = Math.abs(balance.value).toLocaleString('id-ID');

            if (balance.value < 0) {
            return `- ${formattedBalance}`;
            } else if (balance.value > 0) {
            return `+ ${formattedBalance}`;
            } else {
            return formattedBalance;
            }
        });

        async function download_state() {
            if (!cards.value || cards.value.length === 0) {
                showToast({
                    type: 'warning',
                    title: 'No Data',
                    message: 'There is no transaction data to export.',
                });
                return;
            }

            isExporting.value = true;

            try {
                let actionName = 'TransactionReport/export';
                if (selectedExportFormat.value === 'pdf') {
                    actionName = 'TransactionReport/exportPdf';
                } else if (selectedExportFormat.value === 'word') {
                    actionName = 'TransactionReport/exportWord';
                }
                const response = await store.dispatch(actionName);
                await handleDownloadResponse(response);
            } catch (err) {
                const errorMessage = err.response?.data?.message || 'Failed to export transaction report. Please try again.';
                showToast({
                    type: 'error',
                    title: 'Export Failed',
                    message: errorMessage,
                });
            } finally {
                isExporting.value = false;
            }
        }

        function navigateToDetail(card) {
            if (!card || !card.id) return;
            router.push({ name: 'TransactionReportDetail', params: { id: card.id } });
        }

        async function handleDownloadResponse(response) {
            const fileUrl = response?.data?.data;
            if (!fileUrl) {
                showToast({
                    type: 'error',
                    title: 'Export Failed',
                    message: 'Invalid response from server. Please try again.',
                });
                return;
            }

            try {
                const res = await fetch(fileUrl);
                if (!res.ok) throw new Error('Download failed');
                
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const fileName = fileUrl.split('/').pop() || 'report.pdf';

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Download error, falling back to direct link:', error);
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', fileUrl.split('/').pop());
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }

        window.addEventListener('resize', checkMobileView);

        // Function untuk inisialisasi tanggal (31 hari ke belakang dari hari ini)
        const initializeDateRange = () => {
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(today.getDate() - 30);

            dateRange.value = [startDate, today];

            // Jika ada instance flatpickr, update juga
            if (flatpickrInstance) {
                flatpickrInstance.setDate([startDate, today]);
            }

            // Format tanggal ke YYYY-MM-DD
            const formatToYYYYMMDD = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            return [
                formatToYYYYMMDD(startDate),  // index 0: startDate
                formatToYYYYMMDD(today)       // index 1: endDate
            ];
        };

        return {
            selectedBranch,
            selectedMerchant,
            dateRange,
            branches,
            merchants,
            updateMerchants,
            applyFilter,
            clearFilter,
            cards,
            visibleCards,
            columns,
            formatPrice,
            page,
            showBranchPlaceholder,
            showMerchantPlaceholder,
            handleBranchChange,
            handleMerchantChange,
            toggleBranchPlaceholder,
            toggleMerchantPlaceholder,
            handleDateRangeChange,
            datePicker,
            corporateId,
            isDesc,
            isMobile,
            totalPointIn,
            totalPointOut,
            totalSumTransaction,
            thisComponentShow,
            error,
            syncScroll,
            customScrollbar,
            bottomContainer,
            syncCustomScrollbar,
            scrollPercentage,
            meta,
            navPage,
            balance,
            balanceClass,
            balanceDisplay,
            isFilterApplied,
            download_state,
            handleDownloadResponse,
            isExporting,
            showPointManipulation,
            selectedExportFormat,
            navigateToDetail,
            branchDashboard,
            initializeDateRange,
            isPointSystemActive,
            isTransactionReportEnabled,
        };
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobileView);
    },
}
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Header Styles */
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;
}

/* Filter and Action Bar Styling */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 140px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 200px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
    outline: none;
}

/* Date Input Wrapper */
.date-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.date-input-wrapper .form-control-filter {
    padding-right: 36px;
    width: 230px;
}

.calendar-icon-filter {
    position: absolute;
    right: 12px;
    color: var(--primary-color, #FC6666);
    pointer-events: none;
}

/* Sort Toggle Box */
.sort-toggle-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
}

.sort-label {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    transition: color 0.2s;
}

.sort-label.active {
    color: var(--primary-color, #FC6666);
}

/* Small Switch */
.switch-sm {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
}

.switch-sm input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider-sm {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .3s;
    border-radius: 22px;
}

.slider-sm:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
}

input:checked + .slider-sm {
    background-color: var(--primary-color, #FC6666);
}

input:checked + .slider-sm:before {
    transform: translateX(18px);
}

/* Action Section */
.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-apply-filter {
    background-color: var(--primary-color, #FC6666);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-apply-filter:hover {
    filter: brightness(0.9);
    color: white;
}

.btn-clear-filter {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-clear-filter:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

/* Export Action Bar */
.export-action-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background-color: #fff;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.export-format-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.export-label {
    font-family: 'Poppins';
    font-weight: 700;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
}

.format-buttons {
    display: flex;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #E5E7EB;
}

.format-btn {
    padding: 8px 20px;
    border: none;
    background-color: white;
    color: #4A4A4A;
    font-family: 'Poppins';
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border-right: 1px solid #E5E7EB;
}

.format-btn:last-child {
    border-right: none;
}

.format-btn:hover:not(.active) {
    background-color: var(--secondary-color, #FFF1F2);
    color: var(--primary-border, #F93E3E);
}

.format-btn.active {
    background-color: var(--primary-border, #F93E3E);
    color: white;
}

.btn-export {
    background-color: var(--primary-border, #F93E3E);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 8px 24px;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-export:hover {
    filter: brightness(0.9);
    color: white;
}

.btn-export:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Table Styles */
.report-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.report-list-table th,
.report-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.report-row-clickable {
    cursor: pointer;
    transition: background-color 0.2s;
}

.report-row-clickable:hover {
    background-color: var(--secondary-color) !important;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

.branch-text {
    font-weight: 600;
    font-size: 13px;
    color: #374151;
}

.merchant-text {
    font-size: 13px;
    color: #6b7280;
}

.extra-note {
    font-size: 11px;
    font-style: italic;
    color: #aaa;
    margin-top: 2px;
}

.amount-positive {
    font-weight: 700;
    color: #3CAA94;
}

.point-in {
    font-weight: 700;
    color: #3CAA94;
}

.point-out {
    font-weight: 700;
    color: #FC6666;
}

/* Action Buttons in Table */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
}

.btn-detail {
    background-color: #f0f9ff;
    color: #0369a1;
}

.btn-detail:hover {
    background-color: #e0f2fe;
    color: #075985;
}

/* Summary Cards */
.total-summary {
    padding: 0 10px;
}

.summary-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    height: 100%;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-card-inline .summary-content {
    justify-content: flex-start;
}

.summary-card-inline .summary-details {
    flex: unset;
}

.summary-card-inline .summary-label-text {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.summary-inline-value {
    margin-left: auto;
}

.summary-inline-value .summary-value-text {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0;
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.summary-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    flex-shrink: 0;
}

.icon-amount {
    background-color: #3CAA94;
}

.icon-point-in {
    background-color: #10b981;
}

.icon-point-out {
    background-color: #FC6666;
}

.icon-balance {
    background-color: #6366f1;
}

.summary-details {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.summary-label-text {
    font-size: 11px;
    font-weight: 600;
    color: #6c757d;
    margin: 0 0 2px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.summary-value-text {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    color: #212529;
}

/* Flatpickr Override */
.flatpickr-input {
    width: 100%;
    font-weight: 600;
    font-size: 14px;
    border: 1px solid #e2e8f0;
    height: 46px;
    border-radius: 8px;
    padding: 0.375rem 0.75rem;
    background-clip: padding-box;
    background-color: #fff !important;
}

:deep(.mx-input) {
  height: 46px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: none;
  font-weight: 600;
}

:deep(.mx-icon-calendar) {
  color: #FC6666;
}

/* Spinner */
.spinner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    z-index: 200;
}

/* Responsive */
@media (max-width: 991px) {
    .filter-controls {
        flex: 100%;
        flex-direction: column;
        align-items: stretch;
    }

    .filter-item {
        min-width: 100%;
    }

    .date-input-wrapper .form-control-filter {
        width: 100%;
    }

    .action-buttons-group {
        flex: 100%;
        margin-top: 12px;
    }

    .export-action-wrapper {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .export-format-group {
        flex-wrap: wrap;
    }

    .btn-export {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 768px) {
    .report-list-table {
        font-size: 0.8rem;
    }

    .summary-card {
        padding: 14px;
    }

    .summary-value-text {
        font-size: 18px;
    }
}
</style>
